/**
 * Vehicle zone ordering and lateral-side helpers (frontend).
 *
 * Ports the side/mirror logic used server-side in
 * functions/ai-assess-part-damage/ai-assess-part-damage.js so the admin UI can
 * re-side a part when the estimator drags it across the vehicle's centre line
 * (e.g. a left-side quarter dragged into a right-side section).
 */

// Canonical section order: front → back, then top → bottom, left before right
// within each level. Used to sort damage sections into the logical walk-around
// order insurers expect. Keep in sync with ZONE_SORT_ORDER in
// functions/ai-damage-assessment-orchestrator/ai-damage-assessment-orchestrator.js.
export const ZONE_SORT_ORDER = [
  'front',
  'front-left',
  'front-right',
  'left',
  'right',
  'rear-left',
  'rear-right',
  'rear',
  'roof',
  'underside',
  'interior',
  'tray',
  'other',
];

const ZONE_RANK = ZONE_SORT_ORDER.reduce((acc, zone, i) => {
  acc[zone] = i;
  return acc;
}, {});

/** Rank for a zone id; unknown zones sort to the end (stable among themselves). */
export function zoneRank(zoneId) {
  const rank = ZONE_RANK[zoneId];
  return rank === undefined ? ZONE_SORT_ORDER.length : rank;
}

/** Sort a list of zone ids into canonical walk-around order. */
export function sortZones(zoneIds) {
  return [...zoneIds].sort((a, b) => zoneRank(a) - zoneRank(b));
}

/** Lateral side implied by a zone id: 'left', 'right', or 'center'. */
export function getSideFromZone(zone) {
  const z = (zone || '').toLowerCase();
  if (z.includes('left')) return 'left';
  if (z.includes('right')) return 'right';
  return 'center';
}

/**
 * Lateral side named by a part/line item: 'left', 'right', or null.
 * Recognises "(L)"/"(R)" suffixes, Left/Right/LH/RH words, and -lh/-rh ids.
 */
export function getPartSide(name, id) {
  const n = (name || '').toLowerCase();
  const i = (id || '').toLowerCase();
  if (n.includes('(l)') || /\b(left|lh)\b/.test(n) || /-lh$/.test(i)) return 'left';
  if (n.includes('(r)') || /\b(right|rh)\b/.test(n) || /-rh$/.test(i)) return 'right';
  return null;
}

/** Strip side tokens from a name so mirror pairs compare equal. */
export function stripSideTokens(name) {
  return (name || '')
    .toLowerCase()
    .replace(/\((l|r)\)/g, ' ')
    .replace(/\b(left|right|lh|rh)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Flip the side tokens in a display name: "Front Door LH" -> "Front Door RH",
 * "Rear Guard (L)" -> "Rear Guard (R)". Case-preserving for the common tokens.
 * Returns the original string when it carries no recognisable side token.
 */
export function flipNameSide(name, targetSide) {
  if (!name) return name;
  const isRight = targetSide === 'right';
  const swaps = isRight
    ? [[/\bLeft\b/g, 'Right'], [/\bleft\b/g, 'right'], [/\bLH\b/g, 'RH'], [/\blh\b/g, 'rh'], [/\(L\)/g, '(R)'], [/\(l\)/g, '(r)']]
    : [[/\bRight\b/g, 'Left'], [/\bright\b/g, 'left'], [/\bRH\b/g, 'LH'], [/\brh\b/g, 'lh'], [/\(R\)/g, '(L)'], [/\(r\)/g, '(l)']];
  let out = name;
  for (const [pattern, replacement] of swaps) {
    out = out.replace(pattern, replacement);
  }
  return out;
}

/**
 * Find the mirrored line allowance (same part, opposite side) for a part name
 * within a cache of insurer line allowances.
 *
 * @param {string} partName            Current part name
 * @param {Iterable} lineAllowances    Iterable of line allowance docs ({ $id, lineName, primaryLocation })
 * @param {'left'|'right'} targetSide  Side we want the mirror to be on
 * @returns {Object|null} the matching line allowance doc, or null
 */
export function findMirrorLineAllowance(partName, lineAllowances, targetSide) {
  const baseName = stripSideTokens(partName);
  if (!baseName) return null;
  for (const line of lineAllowances) {
    if (getPartSide(line.lineName, line.$id) !== targetSide) continue;
    if (stripSideTokens(line.lineName) === baseName) return line;
  }
  return null;
}
